import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, PrintLabelModal } from '@/components';
import {
  vehiclesApi,
  Vehicle,
  VehicleChangeEntry,
} from '@/services/api/vehicles';
import {
  getVehicleHandoverHistory,
  VehicleHandoverHistory,
} from '@/services/api/handovers';
import { getListRepossessions } from '@/services/api/repossessions';
import { Repossession } from '@/types/common';
import { extractApiErrorMessage } from '@/utils';
import { showErrorToast } from '@/components/toast/globalToast';
import { useListReturnPath } from '@/hooks/useUrlListState';
import {
  VehicleDetailHeader,
  VehicleDetailContent,
  VehicleHistoryLog,
  VehicleIdleCostHeader,
  VehiclePhotoCard,
} from './components/VehicleDetail';

const VehicleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backTo = useListReturnPath('/vehicles');
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [changes, setChanges] = useState<VehicleChangeEntry[]>([]);
  const [handovers, setHandovers] = useState<VehicleHandoverHistory[]>([]);
  const [repossessions, setRepossessions] = useState<Repossession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await vehiclesApi.getVehicle(id);
      const vehicleData =
        (response as any)?.vehicle ||
        (response as any)?.data?.data ||
        (response as any)?.data ||
        response;
      setVehicle(vehicleData);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to load vehicle detail')
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchChanges = useCallback(async () => {
    if (!id) return;
    try {
      const data = await vehiclesApi.getVehicleChanges(id);
      setChanges(data);
    } catch {
      setChanges([]);
    }
  }, [id]);

  const fetchHandovers = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getVehicleHandoverHistory(id);
      setHandovers(data);
    } catch {
      setHandovers([]);
    }
  }, [id]);

  const fetchRepossessions = useCallback(async () => {
    if (!id) return;
    try {
      // History, not a queue: one unit rarely has more than a handful of
      // cases, so one big page beats paginating a timeline.
      const response = await getListRepossessions({
        vehicleID: id,
        size: '100',
      });
      setRepossessions(response.data || []);
    } catch {
      setRepossessions([]);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchDetail();
    fetchChanges();
    fetchHandovers();
    fetchRepossessions();
  }, [fetchDetail, fetchChanges, fetchHandovers, fetchRepossessions, id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 font-medium">
            Loading vehicle details...
          </p>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Vehicle Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The vehicle with ID &ldquo;{id}&rdquo; could not be found or may
            have been deleted.
          </p>
          <Button
            onClick={() => navigate(backTo)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            &larr; Back to Vehicles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto bg-white px-6 py-5">
      <div className="mx-auto">
        <VehicleDetailHeader
          vehicle={vehicle}
          onBack={() => navigate(backTo)}
          actionButton={
            <Button
              variant="white"
              size="small"
              onClick={() => setIsPrintModalOpen(true)}
            >
              <i className="ri-printer-line text-[12px] mr-1" />
              Print Label
            </Button>
          }
        />

        <VehicleIdleCostHeader vehicle={vehicle} onChanged={fetchDetail} />

        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between">
          <div className="w-full basis-[40%] flex-shrink-0 min-w-0 space-y-6">
            <VehicleDetailContent vehicle={vehicle} />
            <VehiclePhotoCard vehicle={vehicle} onChanged={fetchDetail} />
          </div>

          <div className="w-full basis-[60%] flex-shrink-0 min-w-0">
            <VehicleHistoryLog
              changes={changes}
              handovers={handovers}
              repossessions={repossessions}
            />
          </div>
        </div>
      </div>

      <PrintLabelModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        qrValue={String(vehicle.ID ?? vehicle.id ?? '')}
        labelText={vehicle.platNomor || '-'}
      />
    </div>
  );
};

export default VehicleDetailPage;
